// WhatsApp deep-link helper.
// Opens wa.me with a prefilled message. Falls back to native dialer if no phone.

function normalisePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // If it already has country code (11+ digits), keep as is. Otherwise prepend +91.
  if (digits.length >= 11) return digits;
  return `91${digits.slice(-10)}`;
}

export function buildWhatsAppLink(phone, message) {
  const p = normalisePhone(phone);
  const text = encodeURIComponent(message || "");
  return p ? `https://wa.me/${p}?text=${text}` : `https://wa.me/?text=${text}`;
}

export function openWhatsApp(phone, message) {
  const url = buildWhatsAppLink(phone, message);
  window.open(url, "_blank", "noopener,noreferrer");
}

// Auto-send helpers (WaSender-powered via backend).
// These wrap the /api/whatsapp/* endpoints. Callers should render both the manual
// wa.me button (existing) AND an auto-send button; auto-send only shows a warning
// when WaSender is not configured (checked via /api/whatsapp/status).
import { api, formatApiErrorDetail } from "./api";
import { toast } from "sonner";

async function _autoSend(endpoint, body) {
  try {
    const { data } = await api.post(endpoint, body);
    toast.success("WhatsApp sent");
    return data;
  } catch (e) {
    const detail = formatApiErrorDetail(e.response?.data?.detail) || "Failed to send";
    toast.error(detail);
    throw e;
  }
}

export const wa = {
  status: () => api.get("/whatsapp/status").then((r) => r.data),
  sendText: (phone, text, patient_id, type = "manual") => _autoSend("/whatsapp/send-text", { phone, text, patient_id, type }),
  sendInvoice: (invoice_id) => _autoSend(`/whatsapp/invoice/${invoice_id}`, { origin: window.location.origin }),
  sendPrescription: (prescription_id, attachPdf = false) => _autoSend(`/whatsapp/prescription/${prescription_id}`, { origin: window.location.origin, attach_pdf: attachPdf }),
  sendAppointmentReminder: (appointment_id) => _autoSend("/whatsapp/appointment-reminder", { appointment_id }),
  sendBookingConfirmation: (appointment_id) => _autoSend("/whatsapp/booking-confirmation", { appointment_id }),
  sendToken: (invoice_id) => _autoSend("/whatsapp/token", { invoice_id }),
};


// Templates (kept here so future SMS/email integration can reuse the same data shape)
export const TEMPLATES = {
  appointmentReminder: ({ patient_name, clinic_name, appointment_date, appointment_time }) =>
    `Dear ${patient_name || "Patient"},

Reminder: You have an appointment at ${clinic_name || "our clinic"}.

Date: ${appointment_date || "—"}
Time: ${appointment_time || "—"}

Thank you.`,

  invoiceShare: ({ patient_name, clinic_name, amount, link }) =>
    `Hi ${patient_name || "there"}, your invoice from ${clinic_name || "our clinic"} for ₹${amount} is ready.

View / pay here: ${link}

Thank you.`,

  generalMessage: ({ patient_name, clinic_name }) =>
    `Dear ${patient_name || "Patient"},

This is a message from ${clinic_name || "our clinic"}.

`,
};

export function formatAppointmentDateTime(iso) {
  if (!iso) return { date: "—", time: "—" };
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  return { date, time };
}
